'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "editorial": minimalista premium sobre fondo claro. Tipografía
 * serif (Playfair) protagonista, mucho aire, y la foto como pieza ENMARCADA
 * (rounded, con marco) en vez de fondo a sangre. CTA outline. Distinto de
 * classic/vibrant (foto de fondo + overlay). Lee tokens del tema.
 */
export function HeroEditorial({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)

  return (
    <section className="w-full" style={{ background: 'var(--ms-bg)' }}>
      <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-10 text-center">
        {city && (
          <span
            className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] mb-6"
            style={{ color: 'var(--ms-accent)' }}
          >
            <span className="w-[5px] h-[5px] rounded-full" style={{ background: 'var(--ms-accent)' }} />
            {city}
          </span>
        )}

        {/* El nombre va SIEMPRE en serif protagonista. El logo del merchant vive
            en la barra superior (FloatingNav); repetirlo aquí lo duplicaba. */}
        <h1
          className="font-black leading-[1.04] tracking-[-0.01em] text-[40px] sm:text-[64px]"
          style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text)' }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p className="text-base sm:text-lg mt-4 max-w-xl mx-auto" style={{ color: 'var(--ms-text-muted)' }}>
            {tagline}
          </p>
        )}

        {/* Meta discreta: estado · pickup · tiempo */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-5 text-[13px]"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#2ecc71'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : '#b8b0a4',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs — outline editorial */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-[15px] font-bold transition hover:opacity-90"
            style={{ background: 'var(--ms-text)', color: 'var(--ms-bg)' }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center rounded-full px-6 py-3.5 text-[15px] font-semibold transition hover:bg-black/5"
              style={{ border: '1.5px solid var(--ms-text)', color: 'var(--ms-text)' }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>

      {/* Foto enmarcada (no a sangre). El editorial es un tema fotográfico: la
          imagen tiene prioridad sobre el video. El video del hero suele ser un
          reel vertical y, pausado por la política de autoplay del navegador,
          dejaba el marco panorámico vacío; la imagen es más confiable y encaja
          mejor en el marco. */}
      {(heroImageUrl || heroVideoUrl) && (
        <div className="max-w-6xl mx-auto px-6 sm:px-8 pb-16">
          <div
            className="relative w-full overflow-hidden"
            style={{
              aspectRatio: '16 / 7',
              borderRadius: 'calc(var(--ms-radius) * 1.4)',
              boxShadow: '0 30px 60px -30px rgba(26,18,8,0.35)',
            }}
          >
            {heroImageUrl ? (
              <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
            ) : heroVideoUrl ? (
              <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
            ) : null}
          </div>
        </div>
      )}
    </section>
  )
}
